import { useState } from "react";
import { SparkleIcon } from "@phosphor-icons/react";
import { useI18N } from "common/hooks";
import { chatAssistantEnabled } from "common/config";
import { cn } from "@/lib/utils";
import { VenueDataSourceBadge } from "@/components/VenueCard/VenueDataSourceBadge";
import { VenueDetails } from "common/types";

type AccessibilitySummaryCardProps = {
  venueDetails: VenueDetails;
  /** Opens the assistant docked (desktop/tablet) or full-screen (mobile) —
   *  the caller already knows which, same as the existing "Ask AI" buttons. */
  onOpenAssistant: () => void;
  /** Fires place_summary_expanded on expand only, same as before this card moved. */
  onExpand?: (isExpanded: boolean) => void;
};

/**
 * Top-of-tab card on the Accessibility tab: the AI-written summary plus the
 * verified/contributed line. Moved here from the venue's left-hand column to
 * match the native app's layout — previously this content lived above the
 * Contribute button regardless of which tab was open.
 */
export default function AccessibilitySummaryCard({
  venueDetails,
  onOpenAssistant,
  onExpand,
}: AccessibilitySummaryCardProps) {
  const i18n = useI18N();
  const [isExpanded, setIsExpanded] = useState(false);

  const summary = venueDetails?.business?.venue_summary;
  if (!summary) return null;

  return (
    // No border/rounding of its own — the tab panel that hosts this already
    // has one (VenueSheetContent's md:border md:rounded-xl around the whole
    // panel). Each coloured section bleeds past both the tabpanel's own p-4
    // and the extra inset the scrollbar-gutter reservation adds on desktop
    // (measured: 32px total on desktop, 16px on mobile) so the background
    // reaches the panel's true edge, flush against its border. md:-mt-4 does
    // the same for the top edge. The re-applied md:px-8 then lands content
    // at the same 32px inset the filter chips and cards below already use,
    // so icons/text line up instead of sitting 16px further left/right.
    <div className="mb-4">
      {/* Summary + assistant CTA */}
      <div className="flex flex-col md:flex-row md:items-start gap-3 bg-surface-secondary -mx-4 px-5 py-6 md:-mx-8 md:px-8 md:py-5 md:-mt-4">
        <div className="flex-1 flex flex-col gap-2 min-w-0">
          <div className="flex items-center gap-2">
            <SparkleIcon
              size={16}
              weight="fill"
              aria-hidden="true"
              className="text-fg-brand-subtle shrink-0"
            />
            <span className="label-m-semibold text-fg-neutral-strong">
              {i18n.get("accessibilitySummary")}
            </span>
          </div>
          <p
            className={cn(
              "label-m-medium text-fg-neutral-subtle",
              !isExpanded && "line-clamp-2",
            )}
          >
            {summary}{" "}
            <button
              type="button"
              onClick={() => {
                const next = !isExpanded;
                setIsExpanded(next);
                onExpand?.(next);
              }}
              className="label-m-medium text-fg-brand-subtle hover:text-fg-brand-strong underline focus-blue"
            >
              {isExpanded ? i18n.get("viewLess") : i18n.get("viewMore")}
            </button>
          </p>
        </div>

        {/* Same feature flag as "Ask AI" — this is the same assistant, just a
            different entry point, so it can't be visible when that isn't. */}
        {chatAssistantEnabled && (
          <button
            type="button"
            onClick={onOpenAssistant}
            aria-label={i18n.get("askSociabilityBotHint")}
            className="shrink-0 flex items-center justify-center gap-1.5 h-11 md:h-10 px-4 md:pl-3 md:pr-2.5 rounded-lg bg-bg-brand-secondary-default border border-border-token-brand-minimal hover:bg-bg-brand-ghost-hover transition-colors label-m-medium text-fg-neutral-strong"
          >
            {i18n.get("askSociabilityBot")}
            <span className="rounded-full border border-border-token-brand-subtle bg-bg-brand-secondary-default px-2 py-0.5 label-s-medium text-fg-neutral-strong">
              {i18n.get("beta")}
            </span>
          </button>
        )}
      </div>

      {/* Verified / contributed line — desktop only; on mobile this lives
          below the Contribute button instead, per Figma. No divider below
          it — the filter chips that follow provide enough separation. */}
      <div className="hidden md:block bg-bg-brand-secondary-default md:-mx-8 md:px-8 md:py-4">
        <VenueDataSourceBadge
          venue={venueDetails}
          iconClassName="w-4 h-4 shrink-0"
          labelClassName="label-s-medium text-fg-neutral-strong"
        />
      </div>
    </div>
  );
}
